import React, { useMemo, useRef } from "react";
import { MenuItem, Select } from "@mui/material";
import { colorTokens, getSpacing, getTypography } from "../../../designTokens";

const rowsPerPageTypography = getTypography("Body/Small");

/** Standard rows-per-page options for list pagination */
export const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;

/** Default page size when no user preference is stored */
export const DEFAULT_PAGE_SIZE = 25;

/** Read a persisted page size from sessionStorage */
export function getStoredLimit(
  storageKey: string,
  fallback: number = DEFAULT_PAGE_SIZE
): number {
  try {
    const v = Number(sessionStorage.getItem(storageKey));
    return (DEFAULT_ROWS_PER_PAGE_OPTIONS as readonly number[]).includes(v)
      ? v
      : fallback;
  } catch {
    return fallback;
  }
}

/** Persist page size to sessionStorage */
export function persistStoredLimit(storageKey: string, limit: number): void {
  try {
    sessionStorage.setItem(storageKey, String(limit));
  } catch {
    // ignore storage errors
  }
}

/** API pagination shape returned by backend (page, limit, total, totalPages, hasNext, hasPrevious) */
export interface ApiPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface PaginationProps {
  /** Current active page number (1-indexed) - optional when apiPagination is provided */
  currentPage?: number;
  /** Total number of pages - optional when apiPagination is provided */
  totalPages?: number;
  /** Total number of items - optional when apiPagination is provided */
  totalItems?: number;
  /** Number of items per page - optional when apiPagination is provided */
  itemsPerPage?: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Use API response pagination object; overrides currentPage/totalPages/totalItems/itemsPerPage when set */
  apiPagination?: ApiPagination | null;
  /** Number of page buttons to show on each side of current page */
  siblingCount?: number;
  /** Show "Showing X - Y of Z" text */
  showItemsInfo?: boolean;
  /** Custom text for items info (e.g., "patients", "leads", "records") */
  itemsLabel?: string;
  /** Show first/last page buttons */
  showFirstLastButtons?: boolean;
  /** Custom class for container */
  className?: string;
  /** Size variant */
  size?: "small" | "medium" | "large";
  /** Unique ID prefix for testing */
  idPrefix?: string;
  /** Options for rows-per-page selector; used when onLimitChange is provided */
  rowsPerPageOptions?: number[];
  /** Callback when rows per page changes; shows selector when provided */
  onLimitChange?: (limit: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage: currentPageProp,
  totalPages: totalPagesProp,
  totalItems: totalItemsProp,
  itemsPerPage: itemsPerPageProp,
  onPageChange,
  apiPagination,
  siblingCount = 1,
  showItemsInfo = true,
  itemsLabel = "",
  showFirstLastButtons = false,
  className = "",
  size = "medium",
  idPrefix = "pagination",
  rowsPerPageOptions = [...DEFAULT_ROWS_PER_PAGE_OPTIONS],
  onLimitChange,
}) => {
  const currentPage = apiPagination?.page ?? currentPageProp ?? 1;
  const totalPages = apiPagination?.totalPages ?? totalPagesProp ?? 1;
  const totalItems = apiPagination?.total ?? totalItemsProp ?? 0;
  const itemsPerPage = apiPagination?.limit ?? itemsPerPageProp ?? 10;
  const hasNext = apiPagination?.hasNext ?? currentPage < totalPages;
  /** Left arrow: enable whenever not on page 1 so Previous always works when there is a previous page */
  const hasPrevious = currentPage > 1 || (apiPagination?.hasPrevious ?? false);

  /** Throttle arrow clicks so rapid clicks don't queue multiple page changes and cause update loops */
  const lastArrowClickTimeRef = useRef<number>(0);
  const ARROW_CLICK_THROTTLE_MS = 400;

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const sizeClasses = useMemo(() => {
    switch (size) {
      case "small":
        return {
          button: "w-7 h-7 text-[12px]",
          text: "text-[12px]",
          icon: "w-4 h-4",
          padding: "px-4 py-2",
        };
      case "large":
        return {
          button: "w-10 h-10 text-[16px]",
          text: "text-[16px]",
          icon: "w-6 h-6",
          padding: "px-8 py-5",
        };
      default:
        return {
          button: "w-8 h-8 text-[14px]",
          text: "text-[14px]",
          icon: "w-5 h-5",
          padding: "px-6 py-4",
        };
    }
  }, [size]);

  const pageNumbers = useMemo(() => {
    const pages: (number | "ellipsis-start" | "ellipsis-end")[] = [];

    if (totalPages <= 0) return pages;

    // Always show first page
    pages.push(1);

    // Calculate range around current page
    const leftSibling = Math.max(2, currentPage - siblingCount);
    const rightSibling = Math.min(totalPages - 1, currentPage + siblingCount);

    // Add ellipsis after first page if needed
    if (leftSibling > 2) {
      pages.push("ellipsis-start");
    }

    // Add pages around current page
    for (let i = leftSibling; i <= rightSibling; i++) {
      if (i !== 1 && i !== totalPages) {
        pages.push(i);
      }
    }

    // Add ellipsis before last page if needed
    if (rightSibling < totalPages - 1) {
      pages.push("ellipsis-end");
    }

    // Always show last page if more than 1 page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  }, [currentPage, totalPages, siblingCount]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const handlePrevious = () => {
    if (!hasPrevious) return;
    const now = Date.now();
    if (now - lastArrowClickTimeRef.current < ARROW_CLICK_THROTTLE_MS) return;
    lastArrowClickTimeRef.current = now;
    onPageChange(currentPage - 1);
  };

  const handleNext = () => {
    if (!hasNext) return;
    const now = Date.now();
    if (now - lastArrowClickTimeRef.current < ARROW_CLICK_THROTTLE_MS) return;
    lastArrowClickTimeRef.current = now;
    onPageChange(currentPage + 1);
  };

  const handleFirst = () => {
    if (currentPage === 1) return;
    const now = Date.now();
    if (now - lastArrowClickTimeRef.current < ARROW_CLICK_THROTTLE_MS) return;
    lastArrowClickTimeRef.current = now;
    onPageChange(1);
  };

  const handleLast = () => {
    if (currentPage === totalPages) return;
    const now = Date.now();
    if (now - lastArrowClickTimeRef.current < ARROW_CLICK_THROTTLE_MS) return;
    lastArrowClickTimeRef.current = now;
    onPageChange(totalPages);
  };

  if (totalPages <= 0 && totalItems <= 0) {
    return null;
  }

  const itemsInfoText = itemsLabel
    ? `Showing ${startItem} - ${endItem} of ${totalItems} ${itemsLabel}`
    : `Showing ${startItem} - ${endItem} of ${totalItems}`;

  return (
    <div
      className={`flex items-center justify-between border-t border-[var(--Borders-Light,#D4DBE5)] ${sizeClasses.padding} ${className}`}
      id={`${idPrefix}-container`}
      role="navigation"
      aria-label="Pagination"
    >
      <div className="flex items-center gap-4">
        {showItemsInfo && (
          <span
            className={`${sizeClasses.text} font-normal text-[var(--Text-Light-Grey,#647491)] font-outfit`}
            id={`${idPrefix}-info`}
            aria-live="polite"
          >
            {itemsInfoText}
          </span>
        )}
        {onLimitChange && (
          <div
            className="flex items-center gap-2"
            id={`${idPrefix}-rows-per-page-wrapper`}
          >
            <span
              className={`${sizeClasses.text} font-normal text-[var(--Text-Light-Grey,#647491)] font-outfit`}
              id={`${idPrefix}-rows-per-page-label`}
            >
              Rows per page
            </span>
            <Select
              size="small"
              value={itemsPerPage}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              renderValue={(value) => `${value}`}
              id={`${idPrefix}-rows-per-page`}
              aria-label="Rows per page"
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: getSpacing("8px"),
                    mt: 0.5,
                    "& .MuiMenuItem-root": {
                      ...rowsPerPageTypography,
                      fontFamily: `${rowsPerPageTypography.fontFamily}, sans-serif`,
                      color: colorTokens["Text/Black"],
                      minHeight: 32,
                      py: 0.5,
                      px: 1.5,
                    },
                  },
                },
              }}
              sx={{
                minWidth: 72,
                height: 28,
                ...rowsPerPageTypography,
                fontFamily: `${rowsPerPageTypography.fontFamily}, sans-serif`,
                color: colorTokens["Text/Black"],
                backgroundColor: colorTokens["Background/White"],
                borderRadius: getSpacing("8px"),
                "& .MuiOutlinedInput-root": {
                  borderRadius: getSpacing("8px"),
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: getSpacing("8px"),
                  borderColor: colorTokens["Borders/Light"],
                  borderWidth: 1,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: colorTokens["Borders/Light"],
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: colorTokens["Primary/Default"],
                  borderWidth: 1,
                },
                "& .MuiSelect-select": {
                  ...rowsPerPageTypography,
                  fontFamily: `${rowsPerPageTypography.fontFamily}, sans-serif`,
                  py: 0.25,
                  px: 1,
                  minHeight: "unset",
                },
                "& .MuiSelect-icon": {
                  color: colorTokens["Text/Light Grey"],
                  fontSize: rowsPerPageTypography.fontSize,
                  right: 4,
                },
              }}
            >
              {rowsPerPageOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}
      </div>

      <div
        className={`flex items-center gap-1 ${!showItemsInfo ? "mx-auto" : ""}`}
        id={`${idPrefix}-controls`}
      >
        {/* First Page Button */}
        {showFirstLastButtons && (
          <button
            type="button"
            onClick={handleFirst}
            disabled={currentPage === 1}
            className={`${sizeClasses.button} rounded-lg flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`}
            aria-label="Go to first page"
            aria-disabled={currentPage === 1}
            id={`${idPrefix}-first`}
          >
            <svg
              className={`${sizeClasses.icon} text-[var(--Text-Light-Grey,#647491)]`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
              />
            </svg>
          </button>
        )}

        {/* Previous Button */}
        <button
          type="button"
          onClick={handlePrevious}
          disabled={!hasPrevious}
          className={`${sizeClasses.button} rounded-lg flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`}
          aria-label="Go to previous page"
          aria-disabled={!hasPrevious}
          id={`${idPrefix}-prev`}
        >
          <svg
            className={`${sizeClasses.icon} text-[var(--Text-Light-Grey,#647491)]`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        {/* Page Numbers */}
        {pageNumbers.map((page, index) => {
          if (page === "ellipsis-start" || page === "ellipsis-end") {
            return (
              <span
                key={page}
                className={`${sizeClasses.button} flex items-center justify-center ${sizeClasses.text} text-[var(--Text-Light-Grey,#647491)] font-outfit`}
                id={`${idPrefix}-ellipsis-${index}`}
                aria-hidden="true"
              >
                ...
              </span>
            );
          }

          return (
            <button
              key={page}
              type="button"
              onClick={() => handlePageChange(page)}
              className={`${sizeClasses.button} rounded-full ${sizeClasses.text
                  } font-medium font-outfit transition-all duration-200 ${currentPage === page
                  ? "bg-[var(--Primary-Default,#0566FF)] text-white"
                  : "text-[var(--Text-Black,#080f1a)] hover:bg-gray-100"
                }`}
              aria-label={`Go to page ${page}`}
              aria-current={currentPage === page ? "page" : undefined}
              id={`${idPrefix}-page-${page}`}
            >
              {page}
            </button>
          );
        })}

        {/* Next Button */}
        <button
          type="button"
          onClick={handleNext}
          disabled={!hasNext}
          className={`${sizeClasses.button} rounded-lg flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`}
          aria-label="Go to next page"
          aria-disabled={!hasNext}
          id={`${idPrefix}-next`}
        >
          <svg
            className={`${sizeClasses.icon} text-[var(--Text-Light-Grey,#647491)]`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>

        {/* Last Page Button */}
        {showFirstLastButtons && (
          <button
            type="button"
            onClick={handleLast}
            disabled={currentPage === totalPages}
            className={`${sizeClasses.button} rounded-lg flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200`}
            aria-label="Go to last page"
            aria-disabled={currentPage === totalPages}
            id={`${idPrefix}-last`}
          >
            <svg
              className={`${sizeClasses.icon} text-[var(--Text-Light-Grey,#647491)]`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13 5l7 7-7 7M5 5l7 7-7 7"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Spacer for centering when no items info */}
      {!showItemsInfo && <div />}
    </div>
  );
};

export default Pagination;

